import React, { useState, useEffect } from 'react'
import { GetToggleButtonPropsOptions } from 'downshift'
import { css, cx } from '@emotion/css'
import { FormattedMessage, IntlShape } from 'react-intl'
import { DatePicker } from './date-picker'
import { Placement } from '../popper'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../styled'
import { Dropdown } from '../dropdown'
import { Selection } from '../dropdown/selection'
import { defaultMenuRenderer } from '../dropdown/dropdown'
import { I18nConsumer } from '../../components/i18n-provider'

export const toggleTestIDStart = 'toggle-test-id-start'
export const toggleTestIDEnd = 'toggle-test-id-end'
export const datepickerTestID = 'datepicker-test-id'
export const errorTestId = 'error-test-id'

const datePickerContainerCss = css`
  position: absolute;
  z-index: 1;
  padding: 4px 0;
  border-radius: 1px;
  border-width: 1px;
  border-style: solid;
  background-color: ${theme.color.white};
  border-color: ${theme.color.neutral40};
`

const dropdownCss = css`
  flex: 0 0 auto;
  width: auto;
  min-width: 200px;
  line-height: initial;
  margin-right: 50px;
`

const labelCss = css`
  color: ${theme.color.neutral60};
  font-weight: ${deprecatedTheme.fontWeights.primary.bold};
  padding-bottom: 6px;
`

const errorMessageCss = css`
  color: ${theme.color.red50};
  padding-bottom: 6px;
`

const styleOverridesCss = {
  outerContainerCss: css`
    width: unset;
  `,
  selectionContainerCss: css`
    border-radius: 3px;
    background-color: ${theme.color.white};
    background-image: unset;
    border-color: ${theme.color.neutral50};
  `
}

const disabledCss = css`
  pointer-events: none;
  opacity: 0.7;
`

export type JenkinsDateOption =
  | 'LAST_7_DAYS'
  | 'LAST_30_DAYS'
  | 'LAST_6_MONTHS'
  | 'APP_ONBOARD'
  | 'LAST_24_HOURS'
  | 'CUSTOM'
  | 'JOB_START_TIME'

export type JenkinsDateOptionEnd =
  | 'LAST_7_DAYS'
  | 'LAST_14_DAYS'
  | 'LAST_30_DAYS'
  | 'CONTRAST_STEP_RUNS'
  | 'CUSTOM'

export interface ToggleRendererProps {
  readonly getToggleButtonProps: (options?: GetToggleButtonPropsOptions) => any
  readonly isOpen: boolean
  readonly selectedItem?: JenkinsDateOption
  readonly selectedItemEnd?: JenkinsDateOptionEnd
}

export type ToggleRenderer = (
  props: ToggleRendererProps
) => React.ReactElement<any>

const i18nConfigStart: Record<JenkinsDateOption, string> = {
  LAST_7_DAYS: 'detail_lastSevenDays',
  LAST_30_DAYS: 'detail_lastThirtyDays',
  LAST_6_MONTHS: 'detail_lastSixMonths',
  LAST_24_HOURS: 'detail_lastTwentyFourHours',
  APP_ONBOARD: 'detail_applicationOnboarding',
  CUSTOM: 'detail_customOption',
  JOB_START_TIME: 'detail_jenkinsJobStartTime'
}

const startValues: Record<JenkinsDateOption, number> = {
  LAST_7_DAYS: 604800,
  LAST_30_DAYS: 2592000,
  LAST_6_MONTHS: 15552000,
  LAST_24_HOURS: 86400,
  APP_ONBOARD: 0,
  CUSTOM: -1,
  JOB_START_TIME: -2
}

const i18nConfigEnd: Record<JenkinsDateOptionEnd, string> = {
  LAST_7_DAYS: 'detail_lastSevenDays',
  LAST_14_DAYS: 'detail_lastFourteenDays',
  LAST_30_DAYS: 'detail_lastThirtyDays',
  CONTRAST_STEP_RUNS: 'detail_contrastStepRuns',
  CUSTOM: 'detail_customOption'
}

const endValues: Record<JenkinsDateOptionEnd, number> = {
  LAST_7_DAYS: 604800,
  LAST_14_DAYS: 1209600,
  LAST_30_DAYS: 2592000,
  CONTRAST_STEP_RUNS: 0,
  CUSTOM: -1
}

const Label = ({
  children,
  required,
  errorMessage
}: {
  readonly children: React.ReactNode
  readonly required?: boolean
  readonly errorMessage?: boolean
}) => {
  return (
    <div className={errorMessage ? errorMessageCss : labelCss}>{`${children}${
      required ? ' *' : ''
    }`}</div>
  )
}

export interface JenkinsDatePickerDropdownProps {
  readonly required?: boolean
  readonly renderToggle?: ToggleRenderer
  readonly selectedOption: JenkinsDateOption
  readonly selectedOptionEnd: JenkinsDateOptionEnd
  readonly selectedDate: Date
  readonly selectedDateEnd: Date
  readonly onDateOptionSelect: (option: JenkinsDateOption) => void
  readonly onCustomDateSelect: (option: Date) => void
  readonly placement?: Placement
  readonly renderAdditionalMenuContent?: () => React.ReactElement<any>
  readonly setStartDate: (option: object) => void
  readonly setEndDate: (option: object) => void
  readonly invalidTimeRange: (startDate: object, endDate: object) => boolean
  readonly invalidRelativeEndDate: (
    startDate: object,
    endDate: object
  ) => boolean
  readonly selectedStart: number
  readonly selectedEnd: number
  readonly isEdit: boolean
  readonly isCustomStartDate: boolean
  readonly isCustomEndDate: boolean
}

class JenkinsDate {
  public name: string
  public value: number
  public date?: number

  constructor(name, value) {
    this.name = name
    this.value = value
    this.date = new Date().getTime()
  }
}

export function JenkinsDatePickerDropdown({
  ...props
}: JenkinsDatePickerDropdownProps) {
  const { required } = props

  const initDate = new Date()
  initDate.setHours(12, 0, 0)

  const angularStartDate = new JenkinsDate(i18nConfigStart.APP_ONBOARD, 0)
  const angularEndDate = new JenkinsDate(i18nConfigEnd.CONTRAST_STEP_RUNS, 0)

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isCustomPickerOpen, setIsCustomPickerOpen] = useState(false)
  const [isDropdownOpenEnd, setIsDropdownOpenEnd] = useState(false)
  const [isCustomPickerOpenEnd, setIsCustomPickerOpenEnd] = useState(false)
  const [invalidTimeRange, setInvalidTimeRange] = useState(false)
  const [invalidRelativeEnd, setInvalidRelativeEnd] = useState(false)
  const [selectedDate, setSelectedDate] = useState(initDate) // db date
  const [selectedDateEnd, setSelectedDateEnd] = useState(initDate) // db date
  const [selectedOption, setSelectedOption] = useState(
    'APP_ONBOARD' as JenkinsDateOption
  ) // ui selection
  const [selectedOptionEnd, setSelectedOptionEnd] = useState(
    'CONTRAST_STEP_RUNS' as JenkinsDateOptionEnd
  ) // ui selection
  const [startDate, setStartDate] = useState(angularStartDate) // date passed to angular for http request
  const [endDate, setEndDate] = useState(angularEndDate) // date passed to angular for http request

  useEffect(() => {
    initializeDates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <I18nConsumer>
      {({ intl }) => (
        <div>
          <FormattedMessage
            id="title_integrationsSettings_policyVulnerabilityFirstSeen"
            children={(label) => <Label children={label} required={required} />}
          />
          <div
            className={css`
              display: flex;
              width: auto;
              line-height: initial;
              flex-direction: row;
            `}
          >
            <div className={dropdownCss}>
              <FormattedMessage
                id="title_integrationsSettings_policyStartDate"
                children={(label) => (
                  <Label children={label} required={required} />
                )}
              />
              <Dropdown<JenkinsDateOption>
                onOuterClick={onCloseDatePicker}
                onSelect={onSelect}
                isOpen={isDropdownOpen || isCustomPickerOpen}
                styleOverrides={styleOverridesCss}
                items={[
                  'JOB_START_TIME',
                  'LAST_24_HOURS',
                  'LAST_7_DAYS',
                  'LAST_30_DAYS',
                  'LAST_6_MONTHS',
                  'APP_ONBOARD',
                  'CUSTOM'
                ]}
                itemToKey={(item) => i18nConfigStart[item]}
                itemToString={(item) => itemToString(intl, item)}
                selectedItem={selectedOption}
                onChange={onDateOptionSelect}
                itemSelectionToString={(item) =>
                  selectedDate && item === 'CUSTOM'
                    ? selectedDate.toLocaleString()
                    : itemToString(intl, item)
                }
                selectionToggleRenderer={({
                  isOpen,
                  selectionItemRenderer,
                  itemSelectionToString,
                  itemToString,
                  styleOverrides,
                  getToggleButtonProps,
                  selectedItem,
                  hideArrow
                }) => {
                  const getChildren = () => {
                    if (selectedItem !== undefined) {
                      if (selectionItemRenderer) {
                        return selectionItemRenderer({ item: selectedItem })
                      }

                      if (itemSelectionToString) {
                        return itemSelectionToString(selectedItem)
                      }

                      if (itemToString) {
                        return itemToString(selectedItem)
                      }
                    }

                    return ''
                  }
                  return (
                    <div onClick={toggleDropdown}>
                      <Selection
                        data-testid={toggleTestIDStart}
                        isOpen={isOpen}
                        children={getChildren()}
                        containerCss={
                          styleOverrides && styleOverrides.selectionContainerCss
                        }
                        openContainerCss={
                          styleOverrides &&
                          styleOverrides.selectionOpenContainerCss
                        }
                        textCss={
                          styleOverrides && styleOverrides.selectionTextCss
                        }
                        arrowColor={styleOverrides && styleOverrides.arrowColor}
                        hideArrow={hideArrow}
                        {...getToggleButtonProps()}
                      />
                    </div>
                  )
                }}
                menuRenderer={(props) => {
                  if (isCustomPickerOpen) {
                    return (
                      <div
                        data-testid={datepickerTestID}
                        className={cx(
                          'legacy-ds-date-picker-container',
                          datePickerContainerCss
                        )}
                      >
                        <DatePicker
                          {...props}
                          initialDate={new Date(selectedDate)}
                          initialMonth={new Date(selectedDate)}
                          onApply={onDatePickerApply}
                          onCancel={onCloseDatePicker}
                        />
                      </div>
                    )
                  }
                  return defaultMenuRenderer(props)
                }}
              />
            </div>
            <div className={dropdownCss}>
              <FormattedMessage
                id="title_integrationsSettings_policyEndDate"
                children={(label) => (
                  <Label children={label} required={required} />
                )}
              />
              <Dropdown<JenkinsDateOptionEnd>
                onOuterClick={onCloseDatePickerEnd}
                onSelect={onSelectEnd}
                isOpen={isDropdownOpenEnd || isCustomPickerOpenEnd}
                styleOverrides={styleOverridesCss}
                items={[
                  'CONTRAST_STEP_RUNS',
                  'LAST_7_DAYS',
                  'LAST_14_DAYS',
                  'LAST_30_DAYS',
                  'CUSTOM'
                ]}
                itemToKey={(item) => i18nConfigEnd[item]}
                itemToString={(item) => itemToStringEnd(intl, item)}
                selectedItem={selectedOptionEnd}
                onChange={onDateOptionSelectEnd}
                itemSelectionToString={(item) =>
                  selectedDateEnd && item === 'CUSTOM'
                    ? selectedDateEnd.toLocaleString()
                    : itemToStringEnd(intl, item)
                }
                selectionToggleRenderer={({
                  isOpen,
                  selectionItemRenderer,
                  itemSelectionToString,
                  itemToString,
                  styleOverrides,
                  getToggleButtonProps,
                  selectedItem,
                  hideArrow
                }) => {
                  const getChildren = () => {
                    if (selectedItem !== undefined) {
                      if (selectionItemRenderer) {
                        return selectionItemRenderer({ item: selectedItem })
                      }

                      if (itemSelectionToString) {
                        return itemSelectionToString(selectedItem)
                      }

                      if (itemToString) {
                        return itemToString(selectedItem)
                      }
                    }

                    return ''
                  }
                  return (
                    <div
                      onClick={toggleDropdownEnd}
                      className={
                        selectedOption === 'JOB_START_TIME'
                          ? disabledCss
                          : undefined
                      }
                    >
                      <Selection
                        data-testid={toggleTestIDEnd}
                        disabled={true}
                        isOpen={isOpen}
                        children={getChildren()}
                        containerCss={
                          styleOverrides && styleOverrides.selectionContainerCss
                        }
                        openContainerCss={
                          styleOverrides &&
                          styleOverrides.selectionOpenContainerCss
                        }
                        textCss={
                          styleOverrides && styleOverrides.selectionTextCss
                        }
                        arrowColor={styleOverrides && styleOverrides.arrowColor}
                        hideArrow={hideArrow}
                        {...getToggleButtonProps()}
                      />
                    </div>
                  )
                }}
                menuRenderer={(props) => {
                  if (isCustomPickerOpenEnd) {
                    return (
                      <div
                        className={cx(
                          'legacy-ds-date-picker-container',
                          datePickerContainerCss
                        )}
                      >
                        <DatePicker
                          {...props}
                          initialDate={new Date(selectedDateEnd)}
                          initialMonth={new Date(selectedDateEnd)}
                          onApply={onDatePickerApplyEnd}
                          onCancel={onCloseDatePickerEnd}
                        />
                      </div>
                    )
                  }
                  return defaultMenuRenderer(props)
                }}
              />
            </div>
          </div>
          {invalidTimeRange || invalidRelativeEnd ? (
            <div data-testid={errorTestId}>
              <FormattedMessage
                id={
                  invalidRelativeEnd
                    ? 'validation_requiredCustomStartDateWithEndTime'
                    : 'validation_requiredStartTimeBeforeEndTime'
                }
                children={(label) => (
                  <Label
                    children={label}
                    required={required}
                    errorMessage={true}
                  />
                )}
              />
            </div>
          ) : undefined}
        </div>
      )}
    </I18nConsumer>
  )

  function initializeDates() {
    const startDateCheck = new JenkinsDate(i18nConfigStart.APP_ONBOARD, -1)
    const endDateCheck = new JenkinsDate(i18nConfigEnd.CONTRAST_STEP_RUNS, -1)

    if (props.isCustomStartDate) {
      setSelectedOption('CUSTOM' as JenkinsDateOption)
      const startPropsDate = new Date()
      startPropsDate.setTime(props.selectedStart)

      setSelectedDate(startPropsDate)
      setIsDropdownOpen(false)
      setIsCustomPickerOpen(false)

      startDateCheck.value = startValues['CUSTOM' as JenkinsDateOption]
      startDateCheck.date = props.selectedStart
    } else {
      setSelectedOption(
        Object.keys(startValues).find(
          (key) => startValues[key] === props.selectedStart
        ) as JenkinsDateOption
      )

      startDateCheck.value = startValues[selectedOption]
    }

    if (props.isCustomEndDate) {
      setSelectedOptionEnd('CUSTOM' as JenkinsDateOptionEnd)

      const endPropsDate = new Date()
      endPropsDate.setTime(props.selectedEnd)

      setSelectedDateEnd(endPropsDate)
      setIsDropdownOpen(false)
      setIsCustomPickerOpen(false)

      endDateCheck.value = endValues['CUSTOM' as JenkinsDateOption]
      endDateCheck.date = props.selectedEnd
    } else {
      setSelectedOptionEnd(
        Object.keys(endValues).find(
          (key) => endValues[key] === props.selectedEnd
        ) as JenkinsDateOptionEnd
      )
      endDateCheck.value = endValues[selectedOptionEnd]
    }

    setInvalidTimeRange(props.invalidTimeRange(startDateCheck, endDateCheck))
    setStartDate(startDateCheck)
    setEndDate(endDateCheck)
  }

  function toggleDropdown() {
    if (isCustomPickerOpen) {
      setIsDropdownOpen(false)
      setIsCustomPickerOpen(false)
    } else {
      setIsDropdownOpen(!isDropdownOpen)
    }
  }

  function toggleDropdownEnd() {
    if (isCustomPickerOpenEnd) {
      setIsDropdownOpenEnd(false)
      setIsCustomPickerOpenEnd(false)
    } else {
      setIsDropdownOpenEnd(!isDropdownOpenEnd)
    }
  }

  function onSelect(option: JenkinsDateOption) {
    setSelectedOption(option)
    const startDate = new JenkinsDate(
      i18nConfigStart[option],
      startValues[option]
    )

    if (option === 'CUSTOM') {
      setIsDropdownOpen(true)
      setIsCustomPickerOpen(true)

      props.setStartDate(startDate)
      setStartDate(startDate)
      setInvalidTimeRange(props.invalidTimeRange(startDate, endDate))
      setInvalidRelativeEnd(props.invalidRelativeEndDate(startDate, endDate))
    } else if (option === 'JOB_START_TIME') {
      const contrastStepRuns = new JenkinsDate(
        i18nConfigEnd.CONTRAST_STEP_RUNS,
        endValues.CONTRAST_STEP_RUNS
      )

      setEndDate(contrastStepRuns)
      setSelectedOptionEnd('CONTRAST_STEP_RUNS')
      props.setEndDate(endDate)

      props.setStartDate(startDate)
      setStartDate(startDate)
      setInvalidTimeRange(props.invalidTimeRange(startDate, contrastStepRuns))
      setInvalidRelativeEnd(
        props.invalidRelativeEndDate(startDate, contrastStepRuns)
      )
    } else {
      setIsDropdownOpen(!isDropdownOpen)

      props.setStartDate(startDate)
      setStartDate(startDate)
      setInvalidTimeRange(props.invalidTimeRange(startDate, endDate))
      setInvalidRelativeEnd(props.invalidRelativeEndDate(startDate, endDate))
    }
  }

  function onSelectEnd(option: JenkinsDateOptionEnd) {
    setSelectedOptionEnd(option)

    if (option === 'CUSTOM') {
      setIsDropdownOpenEnd(true)
      setIsCustomPickerOpenEnd(true)
    } else {
      setIsDropdownOpenEnd(!isDropdownOpenEnd)
    }

    const endDate = new JenkinsDate(i18nConfigEnd[option], endValues[option])
    props.setEndDate(endDate)
    setEndDate(endDate)
    setInvalidTimeRange(props.invalidTimeRange(startDate, endDate))
    setInvalidRelativeEnd(props.invalidRelativeEndDate(startDate, endDate))
  }

  function itemToString(intl: IntlShape, item: JenkinsDateOption) {
    return intl.formatMessage({ id: i18nConfigStart[item] })
  }

  function itemToStringEnd(intl: IntlShape, item: JenkinsDateOptionEnd) {
    return intl.formatMessage({ id: i18nConfigEnd[item] })
  }

  function onDateOptionSelect(option: JenkinsDateOption) {
    setSelectedOption(option)
    setIsCustomPickerOpen(option === 'CUSTOM')
    setIsDropdownOpen(false)
  }

  function onDateOptionSelectEnd(option: JenkinsDateOptionEnd) {
    setSelectedOptionEnd(option)
    setIsCustomPickerOpenEnd(option === 'CUSTOM')
    setIsDropdownOpenEnd(false)
  }

  function onDatePickerApply(date: Date) {
    setSelectedDate(date)
    setIsDropdownOpen(false)
    setIsCustomPickerOpen(false)

    const startDate = new JenkinsDate(i18nConfigStart.CUSTOM, -1)
    startDate.date = date.getTime()

    props.setStartDate(startDate)

    setStartDate(startDate)
    setInvalidTimeRange(props.invalidTimeRange(startDate, endDate))
    setInvalidRelativeEnd(props.invalidRelativeEndDate(startDate, endDate))
  }

  function onCloseDatePicker() {
    setIsDropdownOpen(false)
    setIsCustomPickerOpen(false)

    setInvalidTimeRange(
      props.invalidTimeRange(
        { value: startDate.value, date: selectedDate.getTime() },
        endDate
      )
    )
    setInvalidRelativeEnd(
      props.invalidRelativeEndDate(
        { value: startDate.value, date: selectedDate.getTime() },
        endDate
      )
    )
  }

  function onDatePickerApplyEnd(date: Date) {
    setSelectedDateEnd(date)
    setIsDropdownOpenEnd(false)
    setIsCustomPickerOpenEnd(false)

    const endDate = new JenkinsDate(i18nConfigEnd.CUSTOM, -1)
    endDate.date = date.getTime()

    props.setEndDate(endDate)
    setEndDate(endDate)
    setInvalidTimeRange(props.invalidTimeRange(startDate, endDate))
    setInvalidRelativeEnd(props.invalidRelativeEndDate(startDate, endDate))
  }

  function onCloseDatePickerEnd() {
    setIsDropdownOpenEnd(false)
    setIsCustomPickerOpenEnd(false)

    setInvalidTimeRange(
      props.invalidTimeRange(startDate, {
        value: endDate.value,
        date: selectedDateEnd.getTime()
      })
    )
    setInvalidRelativeEnd(
      props.invalidRelativeEndDate(startDate, {
        value: endDate.value,
        date: selectedDateEnd.getTime()
      })
    )
  }
}
